import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { api, type ProjectFile, type FileTag } from "@/lib/api";
import { useProject, projectKey } from "./useProjects";

/**
 * Fetch the project files by deriving from the project detail query.
 * No separate list endpoint — files come from GET /v1/projects/:id.
 */
export function useProjectFiles(
  projectId: string | null | undefined,
): UseQueryResult<ProjectFile[]> {
  const { data: project } = useProject(projectId ?? null);

  return useQuery({
    queryKey: ["projectFiles", projectId],
    queryFn: async () => {
      if (!projectId) return [];
      return project?.files ?? [];
    },
    enabled: Boolean(projectId),
  });
}

/**
 * Mutation to update a file's tag.
 */
export function useUpdateFileTag(
  projectId: string,
): UseMutationResult<ProjectFile, Error, { fileId: string; tag: FileTag }> {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ fileId, tag }) => {
      return api.updateFileTag(fileId, tag);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKey(projectId) });
      toast.success("Tag updated. The Summary may need to be re-read.");
    },
    onError: (err) => {
      toast.error((err as Error).message ?? "Couldn't update the tag.");
    },
  });
}
